import React from 'react';
import { css, StyleSheet } from 'aphrodite';
import Dialog from '@material-ui/core/Dialog';
import Fade from '@material-ui/core/Fade';
import CircularProgress from '@material-ui/core/CircularProgress';

import { Helper } from '../../common/helpers/Helper';
import { BaseEntityPage } from '../../common/components/BaseEntityPage';
import { DynamicFormDialog } from '../../common/components/DynamicFormDialog';
import { AppButton } from '../../common/components/AppButton';
import { AppConfig } from '../../AppConfig';
import { AppStyles, AppTheme } from '../../styles/AppTheme';
import { CustomDynamicFormDialogStyles } from '../../styles/CustomStyles';
import { UiHelper } from '../../common/helpers/UiHelper';
import { ApiHelper } from '../../common/helpers/ApiHelper';
import { AppHelper } from '../../helpers/AppHelper';
import { Controller } from '../../common/Controller';

function cleanupJobAction(action) {
  if (action === 'EXPORT_STAFF_LOAD_REPORT') {
    return 'EXPORT_STAFF_ASSIGNMENTS_REPORT'
  } else if (action === 'EXPORT_STAFF_REPORT') {
    return 'EXPORT_STAFF_INTERACTIONS_REPORT'
  }
  return action
}

function isTerminalJobState(state) {
  return state === 'SUCCESS' || state === 'ERROR' || state === 'FAILED'
}

const InplaceHtmlDialogStyles = AppConfig.CUSTOM_STYLE ? CustomDynamicFormDialogStyles : StyleSheet.create({
  customRoot: {
    backgroundColor: 'rgba(0, 0, 0, 0.2)',
    backdropFilter: 'blur(10px)',
    zIndex: 13000,
  },
  customPaper: {
    borderRadius: 40,
    backgroundColor: AppTheme.secondaryBg,
    overflow: 'hidden',
    '::-webkit-scrollbar': {
      display: 'none',
    },
    '@media (max-width: 767px)': {
      borderRadius: 0,
    },
  },
  customPaperWithScrollableBody: {
    display: 'flex',
    flexDirection: 'column',
    maxHeight: '90vh',
    overflow: 'hidden',
    minHeight: 0,
    '@media (max-width: 767px)': {
      maxHeight: 'calc(100% - 140px)',
    },
  },
})

export class JobsCrm extends BaseEntityPage {

  constructor(props) {
    super(props);
    this.ms = AppConfig.CDP_MS.ENDPOINT
    this.pageTitle = 'Jobs'
    this.pageKey = 'jobs'
    this.orgId = this.props.match.params.orgId
    this.jobFieldDef = null
    this.breadCrumbs = [
      { title: Helper.getString('backToCRM'),
        onClick: (e) => {
          if (document.referrer !== '') {
            e.preventDefault();
            this.props.history.goBack();
          }
        },
        to: this.baseRoute() + '/crm-contacts',
        type: 'button', icon: 'arrow_back_ios' },
    ]
    this.toFetch = [
      { ms: AppConfig.CDP_MS.ENDPOINT, method: 'GET', paths: ['miscs', 'get-crm-jobs'], queryParams: { orgId: this.orgId, ...(AppHelper.isTeamsUnifiedView(this.orgId) ? AppHelper.getTeamApiParams(this.orgId) : {}) } },
      { ms: AppConfig.CDP_MS.ENDPOINT, method: 'GET', paths: ['miscs', 'get-crm-job-fielddef'], queryParams: { orgId: this.orgId } },
    ]
    this.tableHeadersMap = {
      Index: (current) => current.__index + 1,
      Job: (current) => {
        let action = cleanupJobAction(current.action)

        return (
          <div className={css(Styles.jobsColumn)}>
            <span className={css(Styles.spanValue)}>{action}</span>
            <br/>
            {current.title && <>
              <span className={css(Styles.spanValue)}>Title: {current.title}</span>
              <br/>
            </>}
            <span className={css(Styles.spanValue)}>Job started at: {Helper.formatTimestamp(current.createdTs)}</span>
            <br/>
          </div>
        )
      },
      Status: (current) => this.renderJobStatusCell(current),
    }
    this.tableHeaders = Object.keys(this.tableHeadersMap)
    this.noAdd = true
    this.apiData = {
      groupId: this.orgId
    }
    this.headerActions = [
      {
        label: 'Refresh',
        icon: 'refresh',
        type: 'icon-label',
        tooltip: 'Refresh',
        onClick: () => this.fetchItems()
      }
    ]
    this.state = {
      ...this.state,
      jobPollingDialog: null,
      pollingIntervalId: null,
      postJobActionLoading: {},
      htmlPreviewJob: null,
    };
  }

  componentWillUnmount() {
    if (this.state.pollingIntervalId) {
      clearInterval(this.state.pollingIntervalId)
    }
  }

  getActionFieldDef() {
    if (!Array.isArray(this.jobFieldDef)) {
      return null
    }
    return this.jobFieldDef.find(field => field && field.key === 'action') || null
  }

  getActionOption(actionKey) {
    const actionField = this.getActionFieldDef()
    if (!actionField || !Array.isArray(actionField.options)) {
      return null
    }
    return actionField.options.find(option => option && option.key === actionKey) || null
  }

  hasInplaceHtml(job) {
    const actionKey = job && job.action
    if (!actionKey) {
      return false
    }
    const actionOption = this.getActionOption(actionKey)
    return !!(actionOption && actionOption.inplacehtml === true)
  }

  getPostJobAction(job) {
    if (job && job._postJobAction) {
      return job._postJobAction
    }
    const actionOption = this.getActionOption(job && job.action)
    return actionOption && actionOption.postJobAction ? actionOption.postJobAction : null
  }

  getPostJobActionCtaLabel(job) {
    if (job && job._postJobActionCtaLabel) {
      return job._postJobActionCtaLabel
    }
    const actionOption = this.getActionOption(job && job.action)
    if (actionOption && actionOption.postJobActionCtaLabel) {
      return actionOption.postJobActionCtaLabel
    }
    return 'Submit'
  }

  isPostJobActionCompleted(job) {
    if (!job) {
      return false
    }
    if (job._postJobActionCompleted === true) {
      return true
    }
    return !!(job.result && job.result.postJobActionCompleted === true)
  }

  canExecutePostJobAction() {
    const roleHelper = Controller.get().roleHelper()
    if (roleHelper.hasCurrentOrgRole('VIEWER')) {
      return false
    }
    if (Controller.get().userMgr().isProductSU()) {
      return true
    }
    return roleHelper.hasCurrentOrgRole('CRM') || roleHelper.hasCurrentOrgRole('SUPERUSER')
  }

  shouldShowPostJobAction(job) {
    if (!job || job.state !== 'SUCCESS' || !this.getPostJobAction(job)) {
      return false
    }
    return !this.isPostJobActionCompleted(job)
  }

  shouldShowJobHtmlPreview(job) {
    return !!(job && job.state === 'SUCCESS' && job.result && job.result.html && this.hasInplaceHtml(job))
  }

  getJobErrorMessage(job) {
    if (!job || !job.result) {
      return 'Unknown'
    }
    if (job.result.payload && job.result.payload.message) {
      return job.result.payload.message
    }
    if (job.result.message) {
      return job.result.message
    }
    return 'Unknown'
  }

  renderJobHtmlPreview(html) {
    if (!html) {
      return null
    }
    return (
      <iframe
        srcDoc={html}
        sandbox=''
        title='Job result preview'
        className={css(Styles.htmlPreviewFrame)}
      />
    )
  }

  renderPostJobActionSection(job, options = {}) {
    const { inModal = false } = options
    if (!job || !this.getPostJobAction(job)) {
      return null
    }

    const jobId = job.id || (this.state.jobPollingDialog && this.state.jobPollingDialog.jobId)
    const isLoading = !!(jobId && this.state.postJobActionLoading[jobId])
    const isCompleted = this.isPostJobActionCompleted(job)
    const emailSentTo = job.result && job.result.emailSentTo

    if (isCompleted) {
      if (!inModal) {
        return this.renderListEmailSentBadge(job)
      }
      return (
        <div className={css(Styles.postJobActionSection)}>
          <span className={css(Styles.emailSentBadge)}>Email sent</span>
          {emailSentTo && (
            <span className={css(Styles.spanValue)}>Email sent to {emailSentTo}</span>
          )}
          {job.result && job.result.postJobActionCompletedAt && (
            <span className={css(Styles.spanValue)}>
              Sent at: {Helper.formatTimestamp(job.result.postJobActionCompletedAt)}
            </span>
          )}
        </div>
      )
    }

    if (!inModal || !this.shouldShowPostJobAction(job) || !this.canExecutePostJobAction()) {
      return null
    }

    return (
      <div className={css(Styles.postJobActionSection)}>
        <AppButton
          buttonText={this.getPostJobActionCtaLabel(job)}
          inProcess={isLoading}
          disabled={isLoading}
          onClick={() => this.executePostJobAction(jobId, job)}
        />
      </div>
    )
  }

  renderListEmailSentBadge(job) {
    const emailSentTo = job.result && job.result.emailSentTo
    const sentAt = job.result && job.result.postJobActionCompletedAt
    if (!emailSentTo) {
      return null
    }

    let sentText = `Email sent to ${emailSentTo}`
    if (sentAt) {
      sentText += ` on ${Helper.formatTimestamp(sentAt)}`
    }

    return (
      <div className={css(Styles.listActionSection)}>
        <span className={css(Styles.spanValue)}>{sentText}</span>
      </div>
    )
  }

  renderViewHtmlButton(job) {
    if (!this.shouldShowJobHtmlPreview(job)) {
      return null
    }
    return (
      <div className={css(Styles.listActionSection)}>
        <span className={css(Styles.spanValue)}>
          <a
            href='#'
            onClick={(e) => {
              e.preventDefault()
              this.openJobHtmlPreview(job)
            }}
          >
            View Html
          </a>
        </span>
      </div>
    )
  }

  openJobHtmlPreview(job) {
    this.setState({ htmlPreviewJob: job })
  }

  closeJobHtmlPreview() {
    this.setState({ htmlPreviewJob: null })
  }

  renderJobResultMeta(job, options = {}) {
    const { showEmailDisclaimer = false } = options
    const result = job && job.result
    if (!result) {
      return null
    }

    return (
      <>
        {result.emailSubject && (
          <>
            <span className={css(Styles.spanValue)}>Subject: {result.emailSubject}</span>
            <br/>
          </>
        )}
        {showEmailDisclaimer && result.emailSentTo && !this.isPostJobActionCompleted(job) && (
          <>
            <span className={css(Styles.spanValue)}>Email will be sent to {result.emailSentTo}</span>
            <br/>
          </>
        )}
      </>
    )
  }

  renderJobStatusCell(current) {
    const errorMessage = this.getJobErrorMessage(current)
    const isFailed = current.state === 'ERROR' || current.state === 'FAILED'

    return (
      <div>
        <span className={css(Styles.spanValue)}>{current.state}</span>
        <br/>
        {current.state === 'SUCCESS' &&
          <>
            {current.result && current.result.message && (
              <>
                <span className={css(Styles.spanValue)}>{current.result.message}</span>
                <br/>
              </>
            )}
            {this.renderJobResultMeta(current)}
            {Array.isArray(current.result && current.result.links) && current.result.links.map((l, index) => (
              <React.Fragment key={`${l.url}-${index}`}>
                <span className={css(Styles.spanValue)}><a href={l.url}>{l.label}</a></span>
                <br/>
              </React.Fragment>
            ))}
            {this.renderViewHtmlButton(current)}
            {this.renderPostJobActionSection(current)}
          </>
        }
        {isFailed &&
          <span>
            <span className={css(Styles.spanValue)}>
              {errorMessage}
            </span>
            <br/>
          </span>
        }
      </div>
    )
  }

  async executePostJobAction(jobId, job) {
    if (!jobId || !this.canExecutePostJobAction()) {
      return
    }

    this.setState(prevState => ({
      postJobActionLoading: {
        ...prevState.postJobActionLoading,
        [jobId]: true,
      },
    }))

    try {
      const response = await ApiHelper.callAwait({
        method: 'POST',
        endPoint: ApiHelper.makeUrlPath2({
          ms: AppConfig.CDP_MS.ENDPOINT,
          paths: ['miscs', 'execute-job-post-action'],
          queryParams: { orgId: this.orgId },
        }),
        jsonBody: { orgId: this.orgId, jobId },
      })

      const message = response.message || 'Email sent successfully'
      const emailSentTo = response.emailSentTo || (job && job.result && job.result.emailSentTo)

      this.setState(prevState => ({
        postJobActionLoading: {
          ...prevState.postJobActionLoading,
          [jobId]: false,
        },
        showToast: true,
        toastMsg: message,
        isErrorToast: false,
        items: Array.isArray(prevState.items) ? prevState.items.map(item => {
          if (item.id !== jobId) {
            return item
          }
          return {
            ...item,
            _postJobActionCompleted: true,
            result: {
              ...item.result,
              postJobActionCompleted: true,
              emailSentTo: emailSentTo || item.result.emailSentTo,
            },
          }
        }) : prevState.items,
        jobPollingDialog: prevState.jobPollingDialog && prevState.jobPollingDialog.jobId === jobId ? {
          ...prevState.jobPollingDialog,
          result: {
            ...prevState.jobPollingDialog.result,
            _postJobActionCompleted: true,
            result: {
              ...(prevState.jobPollingDialog.result.result || {}),
              postJobActionCompleted: true,
              emailSentTo: emailSentTo || (prevState.jobPollingDialog.result.result && prevState.jobPollingDialog.result.result.emailSentTo),
            },
          },
        } : prevState.jobPollingDialog,
        htmlPreviewJob: prevState.htmlPreviewJob && prevState.htmlPreviewJob.id === jobId ? {
          ...prevState.htmlPreviewJob,
          _postJobActionCompleted: true,
          result: {
            ...prevState.htmlPreviewJob.result,
            postJobActionCompleted: true,
            emailSentTo: emailSentTo || prevState.htmlPreviewJob.result.emailSentTo,
          },
        } : prevState.htmlPreviewJob,
      }))

      this.fetchItems()
    } catch (err) {
      this.setState(prevState => ({
        postJobActionLoading: {
          ...prevState.postJobActionLoading,
          [jobId]: false,
        },
        showToast: true,
        toastMsg: Helper.getErrorMsg(err),
        isErrorToast: true,
      }))
    }
  }

  onFetchSuccess(results) {
    if (results && results[1]) {
      this.jobFieldDef = results[1]
    }

    if(AppHelper.isSettingEnabled(this.orgId, 'CRM_JOB_ADD') && results && results[1]) {
      const actionField = results[1].find(result => {
        if(result && result.key === 'action') {
          return result
        }
      })
      if(actionField && Array.isArray(actionField.options) && actionField.options.length > 0) {
        if(this.headerActions && !this.headerActions.some(action => action.label === 'Add Job')) {
          this.headerActions.unshift({
            label: 'Add Job',
            icon: 'add',
            type: 'icon-label',
            tooltip: 'Add Job',
            onClick: () => {
              const fieldDef = {
                title: 'Add Job',
                ctaButtonText: 'Save',
                steps: [
                  {
                    fields: [...results[1]]
                  }
                ]
              }
              this.openModalForAdd(fieldDef)
            }
          })
        }
      }
    }
    this.setState({
      items: results[0].items,
    })
  }

  openModalForAdd(fieldDef) {
    this.apiData = {}

    super.openModalForAdd(fieldDef)
  }

  onAdd() {
    this.setState({
      apiMethod: 'POST',
      apiEndPoint: ApiHelper.makeUrlPath2({
        ms: AppConfig.CDP_MS.ENDPOINT,
        paths: ['miscs', 'create-crm-job'],
        queryParams: { orgId: this.orgId }
      }),
    });
  }


  onTableValue(current, index) {
    if (!this.tableHeaders[index]) { return '' }
    return this.tableHeadersMap[this.tableHeaders[index]](current)
  }

  handleRefreshButtonClick() {
    this.fetchItems()
  }

  onActions(current) {
    const actions = []
    if(current && ['STARTED'].includes(current.state)) {
      actions.push(UiHelper.buttonRefresh(this, current))
    }
    return actions
  }

  closeJobResultsModal() {
    if (this.state.pollingIntervalId) {
      clearInterval(this.state.pollingIntervalId)
    }
    this.setState({
      modalOpen: false,
      jobPollingDialog: null,
      pollingIntervalId: null,
    })
    this.fetchItems()
  }

  async pollJobStatus(jobId) {
    const pollInterval = setInterval(async () => {
      try {
        const jobData = await ApiHelper.callAwait({
          method: 'GET',
          endPoint: ApiHelper.makeUrlPath2({
            ms: AppConfig.CDP_MS.ENDPOINT,
            paths: ['jobs', jobId],
            queryParams: { orgId: this.orgId, groupId: this.groupId },
          }),
        });

        this.setState(prevState => ({
          jobPollingDialog: {
            ...prevState.jobPollingDialog,
            jobId,
            isLoading: !isTerminalJobState(jobData.state),
            result: isTerminalJobState(jobData.state) ? jobData : prevState.jobPollingDialog && prevState.jobPollingDialog.result,
          },
        }));

        if (isTerminalJobState(jobData.state)) {
          clearInterval(pollInterval);
          this.setState({
            jobPollingDialog: {
              jobId,
              isLoading: false,
              result: jobData,
              error: null,
            },
            pollingIntervalId: null,
          });
        }
      } catch (err) {
        clearInterval(pollInterval);
        this.setState({
          jobPollingDialog: {
            jobId,
            isLoading: false,
            result: null,
            error: Helper.getErrorMsg(err),
          },
          pollingIntervalId: null,
        });
      }
    }, 2000);

    this.setState({ pollingIntervalId: pollInterval });
  }

  onCompleteAction(options) {
    const response = options && options.apiInvokeData && options.apiInvokeData.responseBody
    const shouldShowHtmlResults = this.shouldShowInplaceHtmlResults(response)
    if (response && response.id && response.state === 'STARTED' && shouldShowHtmlResults) {
      this.setState({
        jobPollingDialog: {
          jobId: response.id,
          isLoading: true,
          result: null,
          error: null,
        },
      }, () => {
        this.openModalForJobs();
        this.pollJobStatus(response.id);
      });
      return
    }

    if (response && response.state === 'SUCCESS' && shouldShowHtmlResults) {
      this.setState({
        jobPollingDialog: {
          jobId: response.id,
          isLoading: false,
          result: response,
          error: null,
        }
      }, () => {
        this.openModalForJobs();
      });
      return
    }
    this.setState({ modalOpen: false })
    this.fetchItems();
  }

  shouldShowInplaceHtmlResults(response) {
    if (!response || !response.action) {
      return false
    }

    const actionField = this.getActionFieldDef()
    if (!actionField || !actionField.options) {
      if (this.formDefinition && this.formDefinition.steps && this.formDefinition.steps[0]) {
        const modalActionField = this.formDefinition.steps[0].fields.find(f => f.key === 'action')
        if (modalActionField && modalActionField.options) {
          const selectedOption = modalActionField.options.find(o => o.key === response.action)
          return !!(selectedOption && selectedOption.inplacehtml === true)
        }
      }
      return false
    }

    const selectedOption = actionField.options.find(o => o.key === response.action)
    return !!(selectedOption && selectedOption.inplacehtml === true)
  }

  openModalForJobs() {
    this.formDefinition = {
      title: 'Results',
      isResultsModal: true,
    }
    this.setState({ modalOpen: true })
  }

  customSubmit(options) {
    const { formData, caller } = options
    if (this.formDefinition && this.formDefinition.isResultsModal) {
      this.closeJobResultsModal()
      return
    }
    if (formData.isPollingResult) {
      this.setState({ modalOpen: false })
      return
    }
    caller.submit(formData, true)
  }

  renderJobResultsModalContent() {
    const jobPollingDialog = this.state.jobPollingDialog

    if (!jobPollingDialog) {
      return <p className={css(Styles.spanValue)}>No data</p>
    }

    const { isLoading, result, error } = jobPollingDialog

    if (isLoading) {
      return (
        <div className={css(Styles.loadingContainer)}>
          <CircularProgress size={40} />
          <p className={css(Styles.spanValue)}>Loading results...</p>
        </div>
      )
    }

    if (error) {
      return (
        <div className={css(Styles.errorContainer)}>
          <p className={css(Styles.spanValue)}><strong>Error:</strong> {error}</p>
        </div>
      )
    }

    if (result && result.state === 'SUCCESS' && result.result) {
      const jobResult = result.result
      const previewJob = {
        ...result,
        id: result.id || jobPollingDialog.jobId,
      }

      return (
        <div>
          {jobResult.message && (
            <p className={css(Styles.spanValue)}>{jobResult.message}</p>
          )}
          {this.renderJobResultMeta(previewJob, { showEmailDisclaimer: true })}
          {jobResult.html && this.renderJobHtmlPreview(jobResult.html)}
          {this.renderPostJobActionSection(previewJob, { inModal: true })}
        </div>
      )
    }

    return <p className={css(Styles.spanValue)}>No results available</p>
  }

  getInplaceHtmlDialogClasses() {
    return {
      root: css(InplaceHtmlDialogStyles.customRoot),
      paper: css(
        InplaceHtmlDialogStyles.customPaper,
        InplaceHtmlDialogStyles.customPaperWithScrollableBody,
        AppHelper.isDialogScrollbarEnabled() && AppStyles.dialogScrollbar
      ),
    }
  }

  renderJobHtmlPreviewModal() {
    const job = this.state.htmlPreviewJob
    if (!job || !job.result || !job.result.html) {
      return null
    }

    return (
      <Dialog
        open
        aria-labelledby='job-html-preview-modal'
        fullWidth
        TransitionComponent={Fade}
        maxWidth='md'
        classes={this.getInplaceHtmlDialogClasses()}
        onClose={this.closeJobHtmlPreview.bind(this)}
      >
        <div className={css(Styles.resultsModalHeader)}>
          <div className={css(Styles.resultsModalTitle)}>Preview</div>
          <button
            className={css(Styles.modalCloseButton)}
            onClick={this.closeJobHtmlPreview.bind(this)}
            type='button'
          >
            <svg className={css(Styles.modalCloseIcon)} viewBox='0 0 36 36'>
              <path d='M28.5 9.62L26.38 7.5 18 15.88 9.62 7.5 7.5 9.62 15.88 18 7.5 26.38l2.12 2.12L18 20.12l8.38 8.38 2.12-2.12L20.12 18z'></path>
            </svg>
          </button>
        </div>
        <div className={css(Styles.resultsModalContent)}>
          {job.result.message && (
            <p className={css(Styles.spanValue)}>{job.result.message}</p>
          )}
          {this.renderJobResultMeta(job, { showEmailDisclaimer: true })}
          {this.renderJobHtmlPreview(job.result.html)}
          {this.renderPostJobActionSection(job, { inModal: true })}
        </div>
      </Dialog>
    )
  }

  renderBelowTable() {
    return this.renderJobHtmlPreviewModal()
  }

  renderJobResultsModal() {
    return (
      <Dialog
        open={this.state.modalOpen}
        aria-labelledby='job-results-modal'
        fullWidth
        TransitionComponent={Fade}
        maxWidth='md'
        classes={this.getInplaceHtmlDialogClasses()}
        onClose={this.closeJobResultsModal.bind(this)}
      >
        <div className={css(Styles.resultsModalHeader)}>
          <div className={css(Styles.resultsModalTitle)}>Results</div>
          <button
            className={css(Styles.modalCloseButton)}
            onClick={this.closeJobResultsModal.bind(this)}
            type='button'
          >
            <svg className={css(Styles.modalCloseIcon)} viewBox='0 0 36 36'>
              <path d='M28.5 9.62L26.38 7.5 18 15.88 9.62 7.5 7.5 9.62 15.88 18 7.5 26.38l2.12 2.12L18 20.12l8.38 8.38 2.12-2.12L20.12 18z'></path>
            </svg>
          </button>
        </div>
        <div className={css(Styles.resultsModalContent)}>
          {this.renderJobResultsModalContent()}
        </div>
      </Dialog>
    )
  }

  renderModal() {
    if (this.formDefinition && this.formDefinition.isResultsModal) {
      return this.renderJobResultsModal()
    }

    return (
      <DynamicFormDialog
        apiMethod={this.state.apiMethod}
        apiEndPoint={this.state.apiEndPoint}
        uploadLocation={this.uploadLocation || this.orgId}
        uploadMs={this.uploadMs || AppConfig.ENDPOINT}
        customSubmit={this.customSubmit && this.customSubmit.bind(this)}
        onFilterSubmit={this.onFilterSubmit && this.onFilterSubmit.bind(this)}
        apiData={this.state.apiData || this.apiData}
        formDefinition={this.formDefinition}
        customFormComponents={this.customFormComponents}
        isOpen={this.state.modalOpen}
        onClosed={() => this.setState({ modalOpen: false })}
        onFormFieldOptionAdd={this.onFormFieldOptionAdd && this.onFormFieldOptionAdd.bind(this)}
        displayFieldActions={this.displayFieldActions && this.displayFieldActions.bind(this)}
        onCompleteAction={(options) => {
          if (this.onCompleteAction) {
            this.onCompleteAction(options)
          } else {
            this.setState({ modalOpen: false, items: [], dialogApiData: options.apiInvokeData })
            this.fetchItems()
          }
        }}
        fullWidthDialog={this.fullWidthDialog}
      />
    )
  }
}

const Styles = StyleSheet.create({
  spanValue: {
    fontSize: 12
  },
  jobsColumn: {
    minWidth: 300,
    '@media (max-width: 767px)': {
      minWidth: 150,
    },
  },
  htmlPreviewFrame: {
    width: '100%',
    minHeight: 400,
    maxHeight: 560,
    border: '1px solid #ddd',
    borderRadius: 4,
    marginTop: 8,
    marginBottom: 8,
    backgroundColor: '#fff',
  },
  postJobActionSection: {
    marginTop: 8,
    marginBottom: 4,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 6,
    width: '100%',
  },
  listActionSection: {
    marginTop: 4,
    marginBottom: 4,
    textAlign: 'left',
  },
  emailSentBadge: {
    display: 'inline-block',
    fontSize: 11,
    fontWeight: 600,
    color: '#1b5e20',
    backgroundColor: '#e8f5e9',
    borderRadius: 12,
    padding: '2px 10px',
  },
  loadingContainer: {
    textAlign: 'center',
    padding: '40px 20px',
  },
  errorContainer: {
    padding: 20,
    backgroundColor: '#fee',
    borderRadius: 4,
    color: '#c33',
  },
  resultsModalHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '25px 25px 10px',
  },
  resultsModalTitle: {
    color: 'black',
    fontSize: 26,
    fontWeight: 600,
  },
  resultsModalContent: {
    padding: '0 25px 25px',
    maxHeight: '80vh',
    overflowY: 'auto',
  },
  modalCloseButton: {
    border: 'none',
    padding: 0,
    backgroundColor: 'transparent',
    display: 'flex',
    outline: 'none',
    cursor: 'pointer',
  },
  modalCloseIcon: {
    width: 18,
    height: 18,
  },
})
